<template>
    <main class="sap-report">
        <header class="sap-report__header">
            <button type="button" class="icon-button" title="Voltar" @click="back"><ArrowLeftIcon /></button>
            <div>
                <h1>Envios para o SAP</h1>
                <p>Histórico das confirmações de produção da Fockink.</p>
            </div>
        </header>

        <section class="sap-report__filters" aria-label="Filtros do relatório">
            <label class="search-field">
                <SearchIcon />
                <input v-model.trim="filters.search" type="search" placeholder="Pesquisar por ordem, operação, etapa ou erro" @input="scheduleLoad" />
            </label>
            <label><span>Início</span><input v-model="filters.startDate" type="date" @change="loadFirstPage" /></label>
            <label><span>Fim</span><input v-model="filters.endDate" type="date" @change="loadFirstPage" /></label>
            <label>
                <span>Status</span>
                <select v-model="filters.isSuccess" @change="loadFirstPage">
                    <option value="">Todos</option>
                    <option value="true">Sucesso</option>
                    <option value="false">Erro</option>
                </select>
            </label>
        </section>

        <div class="sap-report__summary"><strong>{{ totalCount }}</strong> {{ totalLabel }}</div>

        <section class="sap-report__content" aria-live="polite">
            <div v-if="loading" class="state-message"><LoaderIcon class="spin" /> Carregando envios</div>
            <div v-else-if="error" class="state-message state-message--error">
                <AlertCircleIcon /><span>{{ error }}</span><button type="button" @click="load">Tentar novamente</button>
            </div>
            <div v-else-if="items.length === 0" class="state-message"><InboxIcon /> Nenhum envio encontrado</div>

            <div v-else class="execution-list">
                <article v-for="execution in items" :key="execution.id" :class="['execution', `execution--${execution.status.toLowerCase()}`]">
                    <header class="execution__summary">
                        <div>
                            <small>Ordem / Operação</small>
                            <strong>{{ execution.orderNumber || `Operação ${execution.operationId || "-"}` }}</strong>
                            <span v-if="execution.operationNumber">Op. {{ execution.operationNumber }}</span>
                        </div>
                        <div class="execution__result">
                            <span :class="['status-pill', statusClass(execution)]">{{ statusLabel(execution) }}</span>
                        </div>
                    </header>

                    <ol class="execution__trail" :aria-label="`Etapas do envio da ordem ${execution.orderNumber || execution.operationId}`">
                        <li v-for="step in execution.steps" :key="step.id" :class="['trail-step', step.isSuccess ? 'trail-step--success' : 'trail-step--error']">
                            <button type="button" class="trail-step__button" title="Ver requisição e resposta" @click="openDetail(step)">
                                <span class="trail-step__marker">
                                    <span class="trail-step__dot"><CheckIcon v-if="step.isSuccess" /><XIcon v-else /></span>
                                </span>
                                <strong :title="step.step">{{ step.step }}</strong>
                                <span>{{ step.requestMethod }} · HTTP {{ step.httpStatusCode || "-" }} · {{ step.durationMs }} ms</span>
                                <time :datetime="step.createdAt">{{ formatStepDate(step.createdAt) }}</time>
                                <span v-if="!step.isSuccess && step.sapErrorTitle" class="trail-step__error-hint" :title="step.sapErrorMessage">
                                    {{ step.sapErrorCode ? `${step.sapErrorCode} · ` : "" }}{{ step.sapErrorTitle }}
                                </span>
                            </button>
                        </li>
                    </ol>
                </article>
            </div>
        </section>

        <footer v-if="totalCount > 0" class="sap-report__pagination">
            <label>
                Execuções por página
                <select v-model.number="pageSize" @change="changePageSize"><option :value="1">1</option><option :value="3">3</option><option :value="5">5</option><option :value="10">10</option><option :value="25">25</option><option :value="50">50</option></select>
            </label>
            <span>Exibindo {{ firstVisibleItem }}-{{ lastVisibleItem }} de {{ totalCount }}</span>
            <nav v-if="totalPages > 1" aria-label="Paginação do relatório SAP">
                <button type="button" title="Página anterior" :disabled="page === 1" @click="setPage(page - 1)"><ChevronLeftIcon /></button>
                <button v-for="number in visiblePages" :key="number" type="button" :class="{ active: number === page }" :aria-current="number === page ? 'page' : null" @click="setPage(number)">{{ number }}</button>
                <button type="button" title="Próxima página" :disabled="page === totalPages" @click="setPage(page + 1)"><ChevronRightIcon /></button>
            </nav>
        </footer>

        <div v-if="detailOpen" class="detail-modal" role="dialog" aria-modal="true" aria-label="Detalhes do envio SAP">
            <div class="detail-modal__panel">
                <header>
                    <div><h2>Detalhes do passo #{{ selectedDetail && selectedDetail.id }}</h2><span v-if="selectedDetail">{{ selectedStep ? selectedStep.step : selectedDetail.step }} · {{ formatDate(selectedDetail.createdAt) }}</span></div>
                    <button type="button" title="Fechar" @click="closeDetail"><XIcon /></button>
                </header>
                <div v-if="detailLoading" class="state-message"><LoaderIcon class="spin" /> Carregando detalhes</div>
                <div v-else-if="detailError" class="state-message state-message--error">{{ detailError }}</div>
                <div v-else-if="selectedDetail" class="detail-modal__content">
                    <section v-if="!selectedDetail.isSuccess" class="sap-error-explanation">
                        <div class="sap-error-explanation__title">
                            <AlertCircleIcon />
                            <div>
                                <small>{{ selectedDetail.sapErrorCode || `HTTP ${selectedDetail.httpStatusCode || "-"}` }}</small>
                                <h3>{{ selectedDetail.sapErrorTitle || "O SAP recusou esta etapa" }}</h3>
                            </div>
                        </div>
                        <p v-if="selectedDetail.sapErrorMessage" class="sap-error-explanation__message">{{ selectedDetail.sapErrorMessage }}</p>
                        <div class="sap-error-explanation__guidance">
                            <strong>Como resolver</strong>
                            <p>{{ selectedDetail.sapErrorGuidance || "Revise os dados enviados e tente novamente." }}</p>
                        </div>
                        <span v-if="selectedDetail.sapTransactionId">Transaction ID: <strong>{{ selectedDetail.sapTransactionId }}</strong></span>
                    </section>
                    <dl>
                        <div><dt>Ordem</dt><dd>{{ selectedDetail.orderNumber || "-" }}</dd></div>
                        <div><dt>Operação</dt><dd>{{ selectedDetail.operationNumber || selectedDetail.operationId || "-" }}</dd></div>
                        <div><dt>HTTP</dt><dd>{{ selectedDetail.requestMethod }} {{ selectedDetail.httpStatusCode || "-" }}</dd></div>
                        <div><dt>Duração</dt><dd>{{ selectedDetail.durationMs }} ms</dd></div>
                    </dl>
                    <section><h3>Destino</h3><pre>{{ selectedDetail.requestPath || "-" }}</pre></section>
                    <section><h3>Resposta técnica do SAP</h3><pre>{{ formatPayload(selectedDetail.responsePayload) }}</pre></section>
                    <section><h3>Requisição enviada</h3><pre>{{ formatPayload(selectedDetail.requestPayload) }}</pre></section>
                    <section v-if="selectedDetail.exceptionMessage"><h3>Erro</h3><pre class="detail-modal__error">{{ selectedDetail.exceptionMessage }}</pre></section>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import {
    AlertCircleIcon,
    ArrowLeftIcon,
    CheckIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    InboxIcon,
    LoaderIcon,
    SearchIcon,
    XIcon
} from "vue-feather-icons";
import sapIntegrationReportApi from "./sapIntegrationReportApi";

export default {
    components: {
        AlertCircleIcon,
        ArrowLeftIcon,
        CheckIcon,
        ChevronLeftIcon,
        ChevronRightIcon,
        InboxIcon,
        LoaderIcon,
        SearchIcon,
        XIcon
    },
    data() {
        return {
            items: [],
            loading: false,
            error: "",
            page: 1,
            pageSize: window.innerWidth <= 600 ? 1 : window.innerHeight <= 900 ? 3 : 5,
            totalCount: 0,
            totalPages: 1,
            searchTimer: null,
            filters: { search: "", startDate: "", endDate: "", isSuccess: "" },
            detailOpen: false,
            detailLoading: false,
            detailError: "",
            selectedDetail: null,
            selectedStep: null
        };
    },
    computed: {
        totalLabel() {
            return this.totalCount === 1 ? "execução encontrada" : "execuções encontradas";
        },
        visiblePages() {
            const max = 5;
            let start = Math.max(1, this.page - Math.floor(max / 2));
            const end = Math.min(this.totalPages, start + max - 1);
            start = Math.max(1, end - max + 1);
            return Array.from({ length: end - start + 1 }, (_, index) => start + index);
        },
        firstVisibleItem() {
            return (this.page - 1) * this.pageSize + 1;
        },
        lastVisibleItem() {
            return Math.min(this.page * this.pageSize, this.totalCount);
        }
    },
    async created() {
        const auth = await this.$store.dispatch("auth/fetchPermissions");
        if (auth.accountId !== 23) {
            this.back();
            return;
        }
        await this.load();
    },
    beforeDestroy() {
        window.clearTimeout(this.searchTimer);
    },
    methods: {
        async load() {
            this.loading = true;
            this.error = "";
            try {
                const result = await sapIntegrationReportApi(this.$http2).list({
                    search: this.filters.search || undefined,
                    startDate: this.filters.startDate || undefined,
                    endDate: this.filters.endDate || undefined,
                    isSuccess: this.filters.isSuccess === "" ? undefined : this.filters.isSuccess,
                    page: this.page,
                    pageSize: this.pageSize
                });
                this.items = result.items || [];
                this.page = result.page || 1;
                this.pageSize = result.pageSize || this.pageSize;
                this.totalCount = result.totalCount || 0;
                this.totalPages = result.totalPages || 1;
            } catch (error) {
                this.error = error && error.response && error.response.status === 403
                    ? "Relatório disponível somente para a Fockink."
                    : "Não foi possível carregar os envios para o SAP.";
            } finally {
                this.loading = false;
            }
        },
        loadFirstPage() {
            this.page = 1;
            this.load();
        },
        scheduleLoad() {
            window.clearTimeout(this.searchTimer);
            this.searchTimer = window.setTimeout(this.loadFirstPage, 300);
        },
        setPage(page) {
            this.page = Math.min(Math.max(page, 1), this.totalPages);
            this.load();
        },
        changePageSize() {
            this.page = 1;
            this.load();
        },
        statusLabel(execution) {
            if (execution.status === "SUCCESS") return "Sucesso";
            if (execution.status === "ERROR") return `Falha em ${execution.failureStep}`;
            return "Incompleto";
        },
        statusClass(execution) {
            if (execution.status === "SUCCESS") return "status-pill--success";
            if (execution.status === "ERROR") return "status-pill--error";
            return "status-pill--incomplete";
        },
        async openDetail(step) {
            this.selectedStep = step;
            this.detailOpen = true;
            this.detailLoading = true;
            this.detailError = "";
            this.selectedDetail = null;
            try {
                this.selectedDetail = await sapIntegrationReportApi(this.$http2).detail(step.id);
            } catch (error) {
                this.detailError = "Não foi possível carregar os detalhes deste passo.";
            } finally {
                this.detailLoading = false;
            }
        },
        closeDetail() {
            this.detailOpen = false;
            this.selectedDetail = null;
            this.selectedStep = null;
        },
        formatDate(value) {
            if (!value) return "-";
            return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "medium" }).format(new Date(value));
        },
        formatStepDate(value) {
            if (!value) return "-";
            return new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(new Date(value));
        },
        formatPayload(value) {
            if (!value) return "Sem conteúdo";
            try {
                return JSON.stringify(JSON.parse(value), null, 2);
            } catch (error) {
                return value;
            }
        },
        back() {
            this.$router.push({ name: "operator-orders-menu", params: { prefix: this.$route.params.prefix } });
        }
    }
};
</script>

<style lang="scss" scoped>
.sap-report {
    width: 100%;
    color: #4c4541;

    &__header {
        display: flex; align-items: center; gap: 12px; margin-bottom: 12px;
        h1 { margin: 0; font-family: "Raleway"; font-size: 22px; font-weight: 600; }
        p { margin: 2px 0 0; color: #998f8a; font-size: 12px; }
    }
    &__filters {
        display: grid; grid-template-columns: minmax(260px, 1fr) 150px 150px 150px; align-items: end; gap: 10px; padding: 8px 0; border-top: 1px solid #ece0db; border-bottom: 1px solid #ece0db;
        > label:not(.search-field) { display: grid; gap: 4px; color: #7e7570; font-size: 11px; }
        input[type="date"], select { width: 100%; height: 36px; border: 1px solid #cfc4be; border-radius: 5px; background: #fff; padding: 0 9px; color: #4c4541; }
    }
    &__summary { min-height: 34px; display: flex; align-items: baseline; gap: 6px; padding: 8px 0 5px; color: #7e7570; strong { color: #4c4541; font-size: 17px; } }
    &__pagination {
        min-height: 44px; display: flex; align-items: center; justify-content: space-between; gap: 16px; color: #7e7570; font-size: 12px;
        label, nav { display: flex; align-items: center; gap: 6px; }
        select { height: 32px; border: 1px solid #cfc4be; border-radius: 5px; background: #fff; padding: 0 26px 0 8px; }
        button { width: 32px; height: 32px; border: 1px solid #cfc4be; border-radius: 5px; display: grid; place-items: center; background: #fff; color: #6a5f59; font-weight: 600; }
        button.active { border-color: #974900; background: #974900; color: #fff; }
        button:disabled { border-color: #e8e2df; color: #cfc4be; }
        svg { width: 16px; height: 16px; }
    }
}
.execution { height: 84px; display: grid; grid-template-columns: 190px minmax(0, 1fr); margin-bottom: 4px; overflow: hidden; border: 1px solid #ded8d4; border-left: 3px solid #3e9b5f; border-radius: 6px; background: #fff; }
.execution--error { height: 104px; border-left-color: #c93a31; }
.execution--incomplete { border-left-color: #b97812; }
.execution__summary { display: flex; flex-direction: column; justify-content: space-between; gap: 4px; padding: 7px 10px; border-right: 1px solid #ece5e1; line-height: 1.15; small, span { display: block; color: #8f8580; font-size: 10px; } strong { display: block; margin: 1px 0; overflow: hidden; font-size: 14px; text-overflow: ellipsis; white-space: nowrap; } }
.execution__result { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 7px; }
.execution__trail { min-width: 0; height: 82px; display: grid; grid-template-columns: repeat(5, minmax(100px, 1fr)); align-items: start; margin: 0; padding: 7px 10px; list-style: none; }
.trail-step { position: relative; min-width: 0; padding-right: 10px; color: #776e69; line-height: 1.15;
    &:not(:last-child)::after { content: ""; position: absolute; z-index: 0; top: 9px; left: 22px; right: 0; height: 2px; background: #cee7d6; }
    &:not(:first-child) { border-left: 1px solid #eee8e4; padding-left: 9px;
        &::before { content: ""; position: absolute; z-index: 0; top: 9px; left: 0; width: 16px; height: 2px; background: #cee7d6; }
    }
}
.trail-step__button { position: relative; z-index: 1; width: 100%; min-height: 68px; border: 1px solid transparent; border-radius: 4px; display: block; background: transparent; padding: 1px 6px 3px; color: inherit; font: inherit; line-height: 1.15; text-align: left; transition: background-color 120ms ease, border-color 120ms ease;
    &:hover { border-color: #dfd4ce; background: #faf7f5; }
    &:focus-visible { border-color: #974900; outline: 2px solid rgba(151, 73, 0, 0.16); outline-offset: 1px; }
    > strong, > span, > time { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    > strong { margin-top: 3px; color: #4c4541; font-size: 11px; }
    > span, > time { margin-top: 1px; font-size: 10px; }
    > time { color: #9b918c; }
}
.trail-step--error { color: #a92c24; &:not(:last-child)::after, &:not(:first-child)::before { background: #efc4c0; } .trail-step__button > strong { color: #a92c24; } }
.execution--error .execution__trail { height: 102px; }
.trail-step__error-hint { display: -webkit-box !important; margin-top: 3px !important; overflow: hidden !important; color: #a92c24; font-size: 9px !important; font-weight: 600; white-space: normal !important; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.trail-step__marker { position: relative; z-index: 1; display: flex !important; align-items: center; }
.trail-step__dot { width: 20px; height: 20px; border: 2px solid #3e9b5f; border-radius: 50%; display: grid !important; place-items: center; background: #fff; color: #237a3b;
    svg { width: 12px; height: 12px; stroke-width: 3; }
}
.trail-step--error .trail-step__dot { border-color: #c93a31; color: #c93a31; }
.icon-button { width: 36px; height: 36px; border: 1px solid #cfc4be; border-radius: 5px; display: grid; place-items: center; background: #fff; color: #974900; }
.search-field { width: 100%; height: 36px; border: 1px solid #cfc4be; border-radius: 5px; display: flex; align-items: center; gap: 8px; padding: 0 10px; background: #fff; color: #998f8a;
    input { width: 100%; min-width: 0; border: 0; outline: 0; color: #4c4541; }
    svg { width: 19px; height: 19px; flex: 0 0 auto; }
}
.status-pill { display: inline-flex !important; width: fit-content; min-height: 22px; align-items: center; border-radius: 11px; padding: 2px 8px; font-size: 10px !important; font-weight: 600; }
.status-pill--success { background: #e8f7ed; color: #237a3b !important; }
.status-pill--error { background: #fdecea; color: #b42318 !important; }
.status-pill--incomplete { background: #fff4dc; color: #8a5708 !important; }
.state-message { min-height: 220px; display: flex; align-items: center; justify-content: center; gap: 10px; color: #998f8a; }
.state-message--error { color: #b42318; }
.state-message button { border: 0; background: transparent; color: #974900; text-decoration: underline; }
.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.detail-modal { position: fixed; inset: 0; z-index: 1050; display: grid; place-items: center; padding: 20px; background: rgba(35, 29, 26, 0.54);
    &__panel { width: min(860px, 100%); max-height: calc(100vh - 40px); overflow: auto; border-radius: 7px; background: #fff; box-shadow: 0 18px 50px rgba(35, 29, 26, 0.25); }
    header { min-height: 68px; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 14px 18px; border-bottom: 1px solid #ece0db;
        h2 { margin: 0; font-family: "Raleway"; font-size: 19px; }
        span { color: #998f8a; font-size: 12px; }
        button { width: 36px; height: 36px; border: 0; display: grid; place-items: center; background: transparent; color: #6a5f59; }
    }
    &__content { padding: 18px; }
    dl { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin: 0 0 18px; }
    dt { color: #998f8a; font-size: 11px; font-weight: 500; }
    dd { margin: 3px 0 0; overflow: hidden; font-weight: 600; text-overflow: ellipsis; }
    section + section { margin-top: 14px; }
    h3 { margin: 0 0 6px; font-size: 13px; }
    pre { max-height: 220px; overflow: auto; margin: 0; border: 1px solid #e1dad6; border-radius: 5px; background: #f8f7f6; padding: 12px; color: #4c4541; font-size: 11px; white-space: pre-wrap; overflow-wrap: anywhere; }
    &__error { border-color: #f1b8b2 !important; background: #fff5f4 !important; color: #9d1c13 !important; }
}
.sap-error-explanation { margin-bottom: 18px; border: 1px solid #efc4c0; border-left: 4px solid #c93a31; border-radius: 5px; background: #fff7f6; padding: 14px 16px; color: #6f302b;
    &__title { display: flex; align-items: center; gap: 10px; svg { width: 22px; height: 22px; flex: 0 0 auto; color: #c93a31; } small { color: #a92c24; font-size: 10px; font-weight: 700; } h3 { margin: 1px 0 0; color: #75241e; font-size: 15px; } }
    &__message { margin: 10px 0; font-weight: 600; }
    &__guidance { border-top: 1px solid #f1d5d2; padding-top: 9px; strong { color: #75241e; font-size: 11px; } p { margin: 3px 0 0; } }
    > span { display: block; margin-top: 9px; color: #8e625f; font-size: 10px; overflow-wrap: anywhere; }
}
@media (max-width: 1050px) {
    .execution { grid-template-columns: 165px minmax(0, 1fr); }
    .execution__trail { grid-template-columns: repeat(5, minmax(88px, 1fr)); }
}
@media (max-width: 900px) {
    .sap-report__filters { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    .search-field { grid-column: 1 / -1; }
    .execution { height: auto; grid-template-columns: 1fr; overflow: visible; }
    .execution__summary { flex-direction: row; border-right: 0; border-bottom: 1px solid #ece5e1; }
    .execution__result { justify-content: flex-end; }
    .execution__trail { overflow-x: auto; grid-template-columns: repeat(5, minmax(130px, 1fr)); }
}
@media (max-width: 600px) {
    .sap-report__header { align-items: flex-start; }
    .sap-report__filters { grid-template-columns: 1fr; }
    .search-field { grid-column: auto; }
    .execution__summary { display: grid; }
    .execution__result { justify-content: flex-start; }
    .execution__trail, .execution--error .execution__trail { height: auto; display: block; overflow: visible; padding: 10px 14px; }
    .trail-step { min-height: 68px; padding: 0 0 10px 30px;
        &:not(:last-child)::after { top: 20px; bottom: 0; left: 9px; width: 2px; height: auto; }
        &:not(:first-child) { border-left: 0;
            &::before { display: none; }
        }
        &:not(:last-child) { border-bottom: 1px solid #eee8e4; }
    }
    .trail-step__button { min-height: 58px; padding-left: 0; }
    .trail-step__button > strong { margin: -19px 0 0; }
    .trail-step__button > span:not(.trail-step__marker), .trail-step__button > time { margin-left: 0; }
    .trail-step__marker { margin-left: -30px; }
    .sap-report__pagination { justify-content: center; flex-wrap: wrap; padding: 10px 0; }
    .sap-report__pagination > span { width: 100%; text-align: center; }
    .detail-modal { padding: 0; align-items: end; }
    .detail-modal__panel { max-height: 94vh; border-radius: 7px 7px 0 0; }
    .detail-modal dl { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
